import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'info' | 'error';
  duration?: number | null;
  action?: {
    label: string;
    callback: () => void;
  };
  dismissable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  constructor() {}

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  show(message: string, type: 'success' | 'info' | 'error' = 'info', duration: number | null = 3000, action?: { label: string; callback: () => void }, dismissable: boolean = true): string {
    const toast: Toast = {
      id: this.generateId(),
      message,
      type,
      duration,
      action,
      dismissable
    };

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast]);

    // Autoremove after duration
    if (duration !== null) {
      setTimeout(() => {
        this.remove(toast.id);
      }, duration + 300);
    }

    return toast.id;
  }

  success(message: string, duration?: number | null, action?: { label: string; callback: () => void }, dismissable: boolean = true): string {
    return this.show(message, 'success', duration, action, dismissable);
  }

  error(message: string, duration?: number | null, action?: { label: string; callback: () => void }, dismissable: boolean = true): string {
    return this.show(message, 'error', duration, action, dismissable);
  }

  info(message: string, duration?: number | null, action?: { label: string; callback: () => void }, dismissable: boolean = true): string {
    return this.show(message, 'info', duration, action, dismissable);
  }

  remove(id: string): void {
    const currentToasts = this.toastsSubject.value;
    const updatedToasts = currentToasts.filter(toast => toast.id !== id);
    this.toastsSubject.next(updatedToasts);
  }

  clear(): void {
    this.toastsSubject.next([]);
  }
}
